import { classify } from 'sidecar-utilities'

export const containerStyle = classify({
  group: 'group/select',
  // overflow-auto is needed so a long value is truncated and doesn't make the select bigger
  position: 'relative w-full overflow-auto',
  // overflow-auto crops the outline if its on the select itself. so we use focus-within to
  // add the outline to the parent
  focus:
    'focus-within:outline focus-within:outline-4 focus-within:color-outlinePrimaryDecor',
  outlineBorderRadius: 'rounded-lg',
})

export const labelStyle = ({
  condensed,
  disabled,
  hasError,
  hasIcon,
  hasValues,
  showSelectedSublabel,
}: {
  condensed?: boolean
  disabled?: boolean
  hasError?: boolean
  hasIcon?: boolean
  hasValues?: boolean
  showSelectedSublabel?: boolean
}) => {
  // Hide the label if showSelectedSublabel is true and there is a selected value
  if (condensed || (showSelectedSublabel && hasValues)) {
    return 'hidden'
  }

  let textColor = 'color-textPrimary'
  if (disabled) {
    textColor = 'color-text-disabled'
  } else if (hasError) {
    textColor = 'color-textRisk-default'
  }
  return classify({
    width: 'min-w-fit',
    position: `absolute top-3 ${hasIcon ? 'pl-13' : 'pl-4'}`,
    nonClickable: 'pointer-events-none',
    overflow: 'overflow-hidden',
    color: textColor,
    animation: 'transition-all',
    disabled: 'group-disabled/select:color-text-disabled',
    invalid: 'group-invalid/select:color-textRisk',
    fontSize: 'text-xs',
  })
}

export const selectStyle = ({
  condensed,
  hasError,
}: {
  condensed?: boolean
  hasError?: boolean
}) =>
  classify({
    // needed because our select is actually a button
    align: 'text-left',
    layout: 'w-full flex items-center gap-3',
    padding: 'px-4 py-3',
    height: condensed ? 'h-12' : 'h-16',
    border: 'rounded-lg border color-borderDefaultDecorStrong',
    hover: 'hover:color-borderDefaultDecorStrong-hover',
    active: 'active:color-borderDefaultDecorStrong-active',
    focus: 'focus:color-borderDefaultDecorStrong-active',
    disabled: 'disabled:color-border-disabled',
    error: hasError ? 'color-borderRisk' : '',
    color: 'color-bgDefault color-textOnDefault',
    cursor: 'cursor-pointer',
  })

export const valueStyle = ({
  condensed,
  disabled,
  hasValues,
  showSelectedSublabel,
}: {
  condensed?: boolean
  disabled?: boolean
  hasValues?: boolean
  showSelectedSublabel?: boolean
}) => {
  return classify({
    // Width makes sure the chevron stays to the right
    width: 'w-full min-w-0 truncate',
    // padding makes room for the floating label
    topPadding:
      !condensed && (!showSelectedSublabel || !hasValues) ? 'pt-4' : null,
    bottomPadding:
      !condensed && showSelectedSublabel && hasValues ? 'pb-4' : null,
    // Apply disabled color when disabled
    disabled: disabled ? 'color-text-disabled' : null,
  })
}

export const sublabelStyle = ({ hasIcon }: { hasIcon?: boolean }) =>
  classify({
    size: 'sh-body-xs truncate',
    position: 'absolute right-0',
    topPadding: 'top-9',
    leftPadding: hasIcon ? 'left-13' : 'left-4',
    nonClickable: 'pointer-events-none',
  })

export const iconStyle = (disabled: boolean) =>
  classify({
    width: 'shrink-0',
    color: disabled
      ? 'color-icon-disabled'
      : 'color-icon-default group-hover/select:color-icon-hover group-active/select:color-icon-active group-focus/select:color-icon-hover',
  })

export const popoverStyle = classify({
  border: 'rounded-lg elevation-2nd-floor',
  sizing: 'min-w-30',
  padding: 'p-0',
  // The dropdown paints its own background rather than relying on every child to
  // paint one. Options and section labels still carry theirs (they need it for
  // hover states), but a child that doesn't — or any gap between two of them —
  // would otherwise show the page straight through the card.
  bg: 'color-bgDefaultContainer',
  position: 'z-[800]',
  // When the popover is portaled and rendered as a sibling of a Radix Dialog
  // (i.e. inside a Modal), Radix sets `aria-hidden` and `pointer-events: none`
  // on every sibling of the dialog Content. The portaled popover inherits that
  // and silently drops clicks. Force pointer-events on so options stay clickable.
  pointerEvents: 'pointer-events-auto',
  scroll: 'overflow-x-hidden overflow-y-auto overscroll-contain',
  // Capped to the room floating-ui finds on the anchor's open side, but never
  // below five rows (48px each). The floor is what lets the popover flip: ariakit
  // runs `flip` before `size`, and `flip` measures the element as it is — so a
  // list already squeezed to the space below the field never reads as
  // overflowing and never moves above it, even with a full screen of room there.
  // At five rows it overflows a cramped side, flips to the roomier one, and
  // only then is sized to that side's space. The floor itself yields to the
  // viewport, so on a screen with no five rows on *either* side (a short
  // phone with the keyboard up) the list shrinks to fit rather than running
  // off the bottom.
  height:
    'max-h-[max(var(--popover-available-height),min(15rem,calc(100dvh-2rem)))]',
})

// Used for the combobox's "Type to begin searching..." option.
// It's a non-clickable option.
export const basicOptionStyle = classify({
  group: 'group/item',
  layout: 'flex items-center gap-3',
  padding: 'px-4 py-3',
  bg: 'color-bgDefaultContainer',
})

// Subdued heading above a section of options (e.g. "Search results",
// "Recently searched"). Sits tight to the first row beneath it — the row's own
// top padding supplies the gap, so the label adds none below. Carries the
// container background as well, belt-and-braces with the popover's own.
//
// The margin reset has to be `!m-0`: this is a <p> and it carries `sh-body-xs`,
// so the design system's `p.sh-body-xs { margin-bottom: 0.75rem }` (specificity
// 0,2,0) outranks a plain `.m-0` utility (0,1,0). Without the important the reset
// was inert, leaving a 12px band between the label and the first option.
export const optionSectionLabelStyle = classify({
  text: 'sh-body-xs color-text-weak',
  padding: 'px-4 pt-3 pb-0',
  bg: 'color-bgDefaultContainer',
  reset: '!m-0',
})

export const optionStyle = classify({
  basicOptionStyle,
  cursor: 'cursor-pointer',
  hover: 'hover:color-bgDefaultContainer-hover hover:color-textPrimary-hover',
  // Arrowing through the list highlights the same way hovering does. Ariakit
  // marks the option under the keyboard cursor with `data-active-item`, which no
  // style read before this — so a keyboard user had no idea which option Enter
  // would pick.
  activeItem:
    'data-[active-item]:color-bgDefaultContainer-hover data-[active-item]:color-textPrimary-hover',
  active:
    'active:color-bgDefaultContainer-active active:color-textPrimary-active',
  selected:
    'aria-selected:color-bgDefaultContainer-bold-default aria-selected:sh-strong',
})

export const groupStyle =
  'color-border-weak-default border-t first-of-type:border-none'

export const groupTitleStyle = classify({
  bg: 'color-layer-weak-default',
  padding: 'px-4 py-3',
  Type: 'sh-body-sm sh-all-caps color-textOnDefaultStrong',
})
